import { useState } from 'react';

import { useCurrentUser } from '@/react/hooks/useUser';

import { Button, LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { TextArea } from '@@/form-components/Input/Textarea';
import { Modal } from '@@/modals';
import { Badge } from '@@/Badge';

import { ActiveAlertRow } from './types';
import { useCreateSilence } from './useCreateSilence';
import { SilenceData } from './silenceTypes';

interface Props {
  alert: ActiveAlertRow;
  onSave: (silenceData: SilenceData) => void;
  onCancel: () => void;
}

export function SilenceAlertForm({ alert, onSave, onCancel }: Props) {
  const { user } = useCurrentUser();
  const createSilenceMutation = useCreateSilence();

  // Initialize form with current time + 1 minute buffer and 1 hour duration
  const now = new Date();
  // Add 1 minute buffer to ensure start time is in the future
  const startTime = new Date(now.getTime() + 60 * 1000);
  const oneHourLater = new Date(startTime.getTime() + 60 * 60 * 1000);

  const [formData, setFormData] = useState<SilenceData>({
    comment: `Silencing alert: ${alert.alertName}`,
    createdBy: user?.Username || '', // Automatically set to current user
    startsAt: startTime.toISOString().slice(0, 16), // Format for datetime-local input
    endsAt: oneHourLater.toISOString().slice(0, 16),
    // Automatically set the three required matchers
    matchers: [
      {
        name: 'alertname',
        value: alert.alertName,
        isRegex: false,
        isEqual: true,
      },
      {
        name: 'alert_id',
        value: alert.id,
        isRegex: false,
        isEqual: true,
      },
      {
        name: 'source',
        value: alert.source,
        isRegex: false,
        isEqual: true,
      },
    ],
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      await createSilenceMutation.mutateAsync({
        silenceData: formData,
        // TODO: Get the actual alert manager URL from settings
        // For now, using the default internal AlertManager
        alertManagerURL: 'localhost:9093',
      });

      // Call the onSave callback to close the modal and refresh
      onSave(formData);
    } catch (error) {
      // Error is already handled by the mutation's error handling
      // eslint-disable-next-line no-console
      console.error('Failed to create silence:', error);
    }
  }

  return (
    <>
      <Modal.Header title="Silence Alert" />

      <Modal.Body>
        {/* Silence Details Form */}
        <form onSubmit={handleSubmit} className="form-horizontal">
          <FormControl label="Comment" required inputId="comment">
            <TextArea
              id="comment"
              value={formData.comment}
              onChange={(e) =>
                setFormData({ ...formData, comment: e.target.value })
              }
              placeholder="Reason for silencing this alert..."
              required
              rows={3}
              data-cy="silence-comment-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Created By" required inputId="createdBy">
            <Input
              id="createdBy"
              value={formData.createdBy}
              onChange={(e) =>
                setFormData({ ...formData, createdBy: e.target.value })
              }
              placeholder="Your name or username"
              required
              data-cy="silence-created-by-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Starts At" required inputId="startsAt">
            <Input
              id="startsAt"
              type="datetime-local"
              value={formData.startsAt}
              onChange={(e) =>
                setFormData({ ...formData, startsAt: e.target.value })
              }
              required
              data-cy="silence-starts-at-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          <FormControl label="Ends At" required inputId="endsAt">
            <Input
              id="endsAt"
              type="datetime-local"
              value={formData.endsAt}
              onChange={(e) =>
                setFormData({ ...formData, endsAt: e.target.value })
              }
              required
              data-cy="silence-ends-at-input"
              disabled={createSilenceMutation.isLoading}
            />
          </FormControl>

          {/* Matchers Information (Read-only) */}
          <div className="form-group">
            <label
              className="col-sm-3 col-lg-2 control-label text-left"
              htmlFor="matchers-info"
            >
              Matchers
            </label>
            <div className="col-sm-9 col-lg-10">
              <div id="matchers-info" className="form-control-static">
                <p className="text-muted small mb-3">
                  This silence will match alerts with the following criteria:
                </p>
                {formData.matchers.map((matcher, index) => (
                  <div key={index} className="mb-2">
                    <Badge type="info" className="mr-2">
                      {matcher.name}
                    </Badge>
                    <span className="text-muted">equals</span>
                    <Badge type="muted" className="ml-2">
                      {matcher.value}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          color="default"
          onClick={onCancel}
          data-cy="silence-cancel-button"
          disabled={createSilenceMutation.isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          type="button"
          color="primary"
          onClick={handleSubmit}
          data-cy="silence-submit-button"
          isLoading={createSilenceMutation.isLoading}
          loadingText="Creating..."
        >
          Create Silence
        </LoadingButton>
      </Modal.Footer>
    </>
  );
}
